import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, CreditCard, PiggyBank, BarChart3, Loader2, Receipt, FileText, ArrowUpCircle, ArrowDownCircle, Calendar, ChevronDown } from 'lucide-react';
import { metricsAPI, metricsUtils, handleMetricsAPIError } from './api';

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue-600', trend, isDark, valueColor }) => {
  const colorMap = {
    'blue-600': { border: 'border-blue-600', text: 'text-blue-600', bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50' },
    'green-600': { border: 'border-green-600', text: 'text-green-600', bg: isDark ? 'bg-green-900/30' : 'bg-green-50' },
    'orange-600': { border: 'border-orange-600', text: 'text-orange-600', bg: isDark ? 'bg-orange-900/30' : 'bg-orange-50' },
    'purple-600': { border: 'border-purple-600', text: 'text-purple-600', bg: isDark ? 'bg-purple-900/30' : 'bg-purple-50' },
    'indigo-600': { border: 'border-indigo-600', text: 'text-indigo-600', bg: isDark ? 'bg-indigo-900/30' : 'bg-indigo-50' },
    'green-700': { border: 'border-green-700', text: 'text-green-700', bg: isDark ? 'bg-green-900/30' : 'bg-green-50' },
    'red-600': { border: 'border-red-600', text: 'text-red-600', bg: isDark ? 'bg-red-900/30' : 'bg-red-50' }
  };

  const colors = colorMap[color] || colorMap['blue-600'];

  return (
    <div className={`rounded-lg shadow-sm border h-full ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className={`h-1 rounded-t-lg ${colors.border.replace('border-', 'bg-')}`}></div>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className={`text-3xl font-bold mb-1 ${valueColor || colors.text}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {title}
            </div>
            {subtitle && (
              <div className={`text-xs ${trend?.includes('+') ? 'text-green-600' : isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {subtitle}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialTab = ({ isDark = false, onExplore, onPeriodChange }) => {
  // Load saved filter from localStorage on mount
  const loadSavedFilter = () => {
    try {
      const saved = localStorage.getItem('financialTabFilter');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (error) {
      console.error('Error loading saved filter:', error);
    }
    return null;
  };

  const savedFilter = loadSavedFilter();

  const [selectedPeriod, setSelectedPeriod] = useState(savedFilter?.selectedPeriod || 'month');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(savedFilter?.selectedMonth || null);
  const [startDate, setStartDate] = useState(savedFilter?.startDate || '');
  const [endDate, setEndDate] = useState(savedFilter?.endDate || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);

  // Save filter to localStorage whenever it changes
  const saveFilter = (period, month, start, end) => {
    try {
      const filterData = {
        selectedPeriod: period,
        selectedMonth: month,
        startDate: start,
        endDate: end,
        timestamp: Date.now()
      };
      localStorage.setItem('financialTabFilter', JSON.stringify(filterData));
    } catch (error) {
      console.error('Error saving filter:', error);
    }
  };

  // Get current month info
  const getCurrentMonthInfo = () => {
    const now = new Date();
    const monthName = now.toLocaleString('en-US', { month: 'long' }).toUpperCase();
    const year = now.getFullYear();
    return { monthName, year, fullName: `${monthName} ${year}` };
  };

  // Generate months for the selector (last 12 months + next 3 months)
  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    
    // Generate last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        date: date
      });
    }
    
    // Generate next 3 months
    for (let i = 1; i <= 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        date: date
      });
    }
    
    return months;
  };

  const currentMonth = getCurrentMonthInfo();
  const monthOptions = generateMonthOptions();

  useEffect(() => {
    fetchAllData();
    
    // Notify parent of current filter state on mount (including saved filter)
    if (onPeriodChange) {
      if (selectedMonth) {
        onPeriodChange('custom', { startDate, endDate });
      } else if (selectedPeriod === 'custom' && startDate && endDate) {
        onPeriodChange('custom', { startDate, endDate });
      } else {
        onPeriodChange(selectedPeriod, null);
      }
    }
  }, []);

  useEffect(() => {
    if (!showCustomRange && !selectedMonth) {
      fetchFinancialData();
    }
  }, [selectedPeriod, showCustomRange]);

  // Fetch data when month is selected or dates change (for custom range)
  useEffect(() => {
    if (selectedMonth || (selectedPeriod === 'custom' && startDate && endDate)) {
      fetchFinancialData();
    }
  }, [selectedMonth, startDate, endDate]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchFinancialData(),
        fetchPortfolioData()
      ]);
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      const params = { period: selectedPeriod };
      
      if (selectedPeriod === 'custom' && startDate && endDate) {
        params.start_date = metricsUtils.formatDate(startDate);
        params.end_date = metricsUtils.formatDate(endDate);
      }

      const [financialResponse, cashFlowResponse] = await Promise.all([
        metricsAPI.getFinancialMetrics(params),
        metricsAPI.getCashFlow(params)
      ]);
      
      if (financialResponse.success) {
        setFinancialData(financialResponse.data);
      }
      
      if (cashFlowResponse.success) {
        setCashFlowData(cashFlowResponse.data);
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    }
  };

  const fetchPortfolioData = async () => {
    try {
      const response = await metricsAPI.getPortfolioOverview();
      
      if (response.success) {
        setPortfolioData(response.data);
      }
    } catch (err) {
      console.error('Portfolio data error:', err);
    }
  };

  const handlePeriodClick = (periodValue) => {
    const periodMap = {
      'Today': 'today',
      'This Week': 'week',
      'This Month': 'month',
      'This Year': 'year',
      'Custom Range': 'custom'
    };
    
    const apiPeriod = periodMap[periodValue];
    setSelectedPeriod(apiPeriod);
    setShowCustomRange(apiPeriod === 'custom');
    setSelectedMonth(null); // Clear month selection when changing period
    
    // Save filter
    saveFilter(apiPeriod, null, '', '');
    
    // Notify parent component about period change
    if (onPeriodChange) {
      onPeriodChange(apiPeriod, apiPeriod === 'custom' ? { startDate, endDate } : null);
    }
  };

  const handleMonthSelect = (monthOption) => {
    const year = monthOption.date.getFullYear();
    const month = monthOption.date.getMonth();
    
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    const firstDayStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    
    // Get last day of month
    const lastDay = new Date(year, month + 1, 0);
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
    
    setSelectedMonth(monthOption);
    setSelectedPeriod('custom');
    setStartDate(firstDayStr);
    setEndDate(lastDayStr);
    setShowCustomRange(false);
    setShowMonthSelector(false);
    
    // Save filter
    saveFilter('custom', monthOption, firstDayStr, lastDayStr);
    
    // Notify parent
    if (onPeriodChange) {
      onPeriodChange('custom', { startDate: firstDayStr, endDate: lastDayStr });
    }
    
    // Data will be fetched by useEffect
  };

  const handleApplyDateRange = () => {
    if (startDate && endDate) {
      setSelectedMonth(null); // Clear month selection when using custom range
      
      // Save filter
      saveFilter('custom', null, startDate, endDate);
      
      fetchFinancialData();
      
      // Notify parent component with custom date range
      if (onPeriodChange) {
        onPeriodChange('custom', { startDate, endDate });
      }
    }
  };

  const getPeriodLabel = () => {
    if (selectedMonth) return 'Monthly';
    
    const labelMap = {
      'today': 'Daily',
      'week': 'Weekly',
      'month': 'Monthly',
      'year': 'Yearly',
      'custom': 'Custom'
    };
    return labelMap[selectedPeriod] || 'Monthly';
  };

  const getDisplayPeriod = () => {
    if (selectedMonth) return selectedMonth.label;
    
    const displayMap = {
      'today': 'Today',
      'week': 'This Week',
      'month': 'This Month',
      'year': 'This Year',
      'custom': 'Custom Range'
    };
    return displayMap[selectedPeriod] || 'This Month';
  };

  // Format value with K for thousands and M for millions
  const formatValue = (value) => {
    const num = parseFloat(value) || 0;
    if (num >= 1000000) {
      return `KSh ${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `KSh ${(num / 1000).toFixed(0)}K`;
    }
    return `KSh ${num.toLocaleString()}`;
  };

  // Calculate overall risk
  const calculateOverallRisk = () => {
    if (!portfolioData) return { value: 0, isPositive: true };
    
    const savings = portfolioData.total_savings?.value || 0;
    const loans = portfolioData.total_loans?.value || 0;
    const advances = portfolioData.total_advances?.value || 0;
    
    const risk = savings - (loans + advances);
    return {
      value: risk,
      isPositive: risk >= 0
    };
  };

  // Calculate Gross P&L (without expenses)
  const calculateGrossPnL = () => {
    if (!financialData) return 0;
    
    const interestOnLoans = financialData.interest_on_loans?.value || 0;
    const advanceInterest = financialData.advance_interest?.value || 0;
    const totalOthers = financialData.total_others?.value || 0;
    
    return interestOnLoans + advanceInterest + totalOthers;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading financial data...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const pnlValue = financialData?.pnl?.value || 0;
  const isPnlPositive = pnlValue >= 0;
  const grossPnL = calculateGrossPnL();
  const isGrossPnLPositive = grossPnL >= 0;
  const overallRisk = calculateOverallRisk();

  return (
    <div>
      {/* Modern Filter Buttons */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className={`inline-flex rounded-xl p-1.5 gap-2 ${isDark ? 'bg-[#1e2329]' : 'bg-gray-100'}`}>
          {['Today', 'This Week', 'This Month', 'This Year', 'Custom Range'].map((period) => (
            <button
              key={period}
              onClick={() => handlePeriodClick(period)}
              className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                getDisplayPeriod() === period && !selectedMonth
                  ? isDark
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50'
                    : 'bg-white text-blue-600 shadow-md'
                  : isDark
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-[#2b323c]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              {period.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Current Month Display */}
        {selectedPeriod === 'month' && !selectedMonth && (
          <div className={`px-6 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 ${
            isDark 
              ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-2 border-purple-500 text-purple-300' 
              : 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-400 text-purple-700'
          }`}>
            {currentMonth.fullName}
          </div>
        )}

        {/* Month Selector Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowMonthSelector(!showMonthSelector)}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
              selectedMonth
                ? isDark
                  ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-2 border-green-500 text-green-300'
                  : 'bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-400 text-green-700'
                : isDark
                ? 'bg-[#2b323c] text-gray-300 hover:bg-gray-700 border border-gray-600'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            <Calendar className="w-4 h-4" />
            {selectedMonth ? selectedMonth.label : 'Select Month'}
            <ChevronDown className={`w-4 h-4 transition-transform ${showMonthSelector ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {showMonthSelector && (
            <div className={`absolute top-full mt-2 w-64 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto ${
              isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
              {monthOptions.map((month) => (
                <button
                  key={month.value}
                  onClick={() => handleMonthSelect(month)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selectedMonth?.value === month.value
                      ? isDark
                        ? 'bg-blue-900/30 text-blue-400'
                        : 'bg-blue-50 text-blue-600'
                      : isDark
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {month.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear Filter Button (shows when filter is saved and different from default) */}
        {savedFilter && (savedFilter.selectedPeriod !== 'month' || savedFilter.selectedMonth) && (
          <button
            onClick={() => {
              localStorage.removeItem('financialTabFilter');
              setSelectedPeriod('month');
              setSelectedMonth(null);
              setStartDate('');
              setEndDate('');
              setShowCustomRange(false);
              if (onPeriodChange) {
                onPeriodChange('month', null);
              }
              fetchFinancialData();
            }}
            className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
              isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
            }`}
            title="Reset to This Month"
          >
            ✕ Clear Filter
          </button>
        )}
        
        <button 
          onClick={() => {
            // Ensure parent has latest filter state before exploring
            if (onPeriodChange) {
              if (selectedMonth) {
                onPeriodChange('custom', { startDate, endDate });
              } else if (selectedPeriod === 'custom' && startDate && endDate) {
                onPeriodChange('custom', { startDate, endDate });
              } else {
                onPeriodChange(selectedPeriod, null);
              }
            }
            // Small delay to ensure state is updated
            setTimeout(() => {
              onExplore();
            }, 50);
          }}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors shadow-lg flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Explore
        </button>
      </div>

      {/* Custom Date Range Picker */}
      {showCustomRange && (
        <div className={`rounded-lg p-6 mb-6 ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-[#1e2329] border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-[#1e2329] border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium mb-2 opacity-0">Apply</label>
              <button 
                onClick={handleApplyDateRange}
                disabled={!startDate || !endDate}
                className="w-full px-8 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Date Range
              </button>
            </div>
          </div>
        </div>
      )}

      {financialData && (
        <>
          {/* Performance */}
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {getPeriodLabel()} Performance
          </h3>
          
          {/* First row - 4 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <MetricCard
              title={`Loans Repaid ${selectedMonth ? selectedMonth.label : getDisplayPeriod()}`}
              value={`KSh ${(financialData.loans_repaid?.value || 0).toLocaleString()}`}
              subtitle={financialData.loans_repaid?.change || 'N/A'}
              icon={DollarSign}
              color="green-600"
              isDark={isDark}
            />
            <MetricCard
              title="Interest on Loans"
              value={`KSh ${(financialData.interest_on_loans?.value || 0).toLocaleString()}`}
              subtitle={financialData.interest_on_loans?.change || 'N/A'}
              icon={TrendingUp}
              color="blue-600"
              isDark={isDark}
            />
            <MetricCard
              title="Advance Loans Repaid"
              value={`KSh ${(financialData.advance_repaid?.value || 0).toLocaleString()}`}
              subtitle={financialData.advance_repaid?.change || 'N/A'}
              icon={CreditCard}
              color="orange-600"
              isDark={isDark}
            />
            <MetricCard
              title="Advance Interest"
              value={`KSh ${(financialData.advance_interest?.value || 0).toLocaleString()}`}
              subtitle={financialData.advance_interest?.change || 'N/A'}
              icon={TrendingUp}
              color="purple-600"
              isDark={isDark}
            />
          </div>

          {/* Second row - 4 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Total Others"
              value={`KSh ${(financialData.total_others?.value || 0).toLocaleString()}`}
              subtitle={financialData.total_others?.change || 'N/A'}
              icon={FileText}
              color="indigo-600"
              isDark={isDark}
            />
            <MetricCard
              title="Gross Realized P&L"
              value={`KSh ${grossPnL.toLocaleString()}`}
              subtitle="Interest income + Others (before expenses)"
              icon={BarChart3}
              color={isGrossPnLPositive ? "green-600" : "red-600"}
              valueColor={isGrossPnLPositive ? "text-green-600" : "text-red-600"}
              isDark={isDark}
            />
            <MetricCard
              title="Total Expenses"
              value={`KSh ${(financialData.total_expenses?.value || 0).toLocaleString()}`}
              subtitle={financialData.total_expenses?.change || 'N/A'}
              icon={Receipt}
              color="red-600"
              isDark={isDark}
            />
            <MetricCard
              title="Net Realized P&L"
              value={`KSh ${(pnlValue || 0).toLocaleString()}`}
              subtitle={financialData.pnl?.change || 'N/A'}
              icon={BarChart3}
              color={isPnlPositive ? "green-600" : "red-600"}
              valueColor={isPnlPositive ? "text-green-600" : "text-red-600"}
              isDark={isDark}
            />
          </div>

          {/* Collections Overview */}
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Collections Overview
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <MetricCard
              title="Cash Collection"
              value={`KSh ${(financialData.cash_collection?.value || 0).toLocaleString()}`}
              icon={DollarSign}
              color="green-600"
              isDark={isDark}
            />
            <MetricCard
              title="Online Collection"
              value={`KSh ${(financialData.online_collection?.value || 0).toLocaleString()}`}
              icon={CreditCard}
              color="blue-600"
              isDark={isDark}
            />
            <MetricCard
              title={`Total ${getPeriodLabel()} Collection`}
              value={`KSh ${(financialData.total_collection?.value || 0).toLocaleString()}`}
              icon={PiggyBank}
              color="green-700"
              isDark={isDark}
            />
          </div>
        </>
      )}

      {/* Cash Flow Analysis */}
      {(cashFlowData || financialData) && (
        <div className={`rounded-lg shadow-sm border p-6 mb-8 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Cash Flow Analysis
          </h3>
          
          {/* Top Row - Savings and Disbursements */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div 
              className="text-center p-6 rounded-lg" 
              style={{ 
                backgroundColor: isDark ? '#10b98120' : '#10b98110', 
                border: '2px solid #10b98130' 
              }}
            >
              <div className="flex items-center justify-center mb-2">
                <PiggyBank className="w-6 h-6 mr-2" style={{ color: '#10b981' }} />
              </div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#10b981' }}>
                KSh {(financialData?.total_savings?.value || 0).toLocaleString()}
              </div>
              <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                Total Savings ({getPeriodLabel()})
              </div>
            </div>
            
            <div 
              className="text-center p-6 rounded-lg" 
              style={{ 
                backgroundColor: isDark ? '#3b82f620' : '#3b82f610', 
                border: '2px solid #3b82f630' 
              }}
            >
              <div className="flex items-center justify-center mb-2">
                <ArrowUpCircle className="w-6 h-6 mr-2" style={{ color: '#3b82f6' }} />
              </div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#3b82f6' }}>
                KSh {(financialData?.loans_disbursed?.value || 0).toLocaleString()}
              </div>
              <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                Loans Disbursed ({getPeriodLabel()})
              </div>
            </div>
            
            <div 
              className="text-center p-6 rounded-lg" 
              style={{ 
                backgroundColor: isDark ? '#f59e0b20' : '#f59e0b10', 
                border: '2px solid #f59e0b30' 
              }}
            >
              <div className="flex items-center justify-center mb-2">
                <ArrowDownCircle className="w-6 h-6 mr-2" style={{ color: '#f59e0b' }} />
              </div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#f59e0b' }}>
                KSh {(financialData?.advance_disbursed?.value || 0).toLocaleString()}
              </div>
              <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                Advance Loans Disbursed ({getPeriodLabel()})
              </div>
            </div>
          </div>

          {/* Original Cash Flow Row */}
          {cashFlowData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {cashFlowData.map((item, index) => (
                <div 
                  key={index} 
                  className="text-center p-6 rounded-lg" 
                  style={{ 
                    backgroundColor: isDark ? `${item.color}20` : `${item.color}10`, 
                    border: `2px solid ${item.color}30` 
                  }}
                >
                  <div className="text-3xl font-bold mb-2" style={{ color: item.color }}>
                    KSh {item.value.toLocaleString()}
                  </div>
                  <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>{item.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Portfolio Overview */}
      {portfolioData && (
        <>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Portfolio Overview
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Loans Outstanding"
              value={formatValue(portfolioData.total_loans?.value || 0)}
              subtitle={portfolioData.total_loans?.change || 'N/A'}
              icon={CreditCard}
              color="blue-600"
              isDark={isDark}
            />
            <MetricCard
              title="Total Advance Loans"
              value={formatValue(portfolioData.total_advances?.value || 0)}
              subtitle={portfolioData.total_advances?.change || 'N/A'}
              icon={CreditCard}
              color="orange-600"
              isDark={isDark}
            />
            <MetricCard
              title="Total Savings"
              value={formatValue(portfolioData.total_savings?.value || 0)}
              subtitle={portfolioData.total_savings?.change || 'N/A'}
              icon={PiggyBank}
              color="green-600"
              isDark={isDark}
            />
            <MetricCard
              title="Overall Risk"
              value={formatValue(overallRisk.value)}
              subtitle={portfolioData.total_savings?.change || 'N/A'}
              icon={TrendingUp}
              color={overallRisk.isPositive ? "green-600" : "red-600"}
              valueColor={overallRisk.isPositive ? "text-green-600" : "text-red-600"}
              isDark={isDark}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialTab;